import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Play } from "lucide-react";
import { NAV_LINKS } from "../../lib/site";
import { useLead, scrollToId, BrutalButton } from "./shared";

export default function Nav() {
  const { openLead } = useLead();
  const [scrolled, setScrolled] = useState(false);
  const [menu, setMenu] = useState(false);
  const [active, setActive] = useState("");

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 40);
      let current = "";
      for (const l of NAV_LINKS) {
        const el = document.getElementById(l.id);
        if (el && el.getBoundingClientRect().top <= 120) current = l.id;
      }
      setActive(current);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setMenu(false);
    scrollToId(id);
  };

  return (
    <motion.header
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 inset-x-0 z-[80] transition-[background-color,box-shadow,border-color] duration-300 ${
        scrolled
          ? "bg-brand-cream/80 backdrop-blur-xl border-b-2 border-brand-black/10 shadow-[0_6px_24px_rgba(20,20,20,0.06)]"
          : "bg-transparent border-b-2 border-transparent"
      }`}
      data-testid="site-nav"
    >
      <div className="max-w-[1320px] mx-auto px-5 sm:px-8 md:px-12 h-[64px] flex items-center justify-between gap-4">
        {/* Logo */}
        <button onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} className="flex items-center gap-2 shrink-0" data-testid="nav-logo">
          <span className="h-8 w-8 rounded-lg bg-brand-red flex items-center justify-center border-2 border-brand-black">
            <Play size={16} className="text-white fill-white ml-0.5" />
          </span>
          <span className="font-display text-lg tracking-tight">Ratish Jha</span>
        </button>

        {/* Desktop links */}
        <nav className="hidden lg:flex items-center gap-1">
          {NAV_LINKS.map((l) => (
            <button
              key={l.id}
              onClick={() => go(l.id)}
              data-testid={`nav-link-${l.id}`}
              className={`relative px-3 py-2 text-sm font-semibold transition-colors ${
                active === l.id ? "text-brand-blue" : "text-brand-ink hover:text-brand-blue"
              }`}
            >
              {l.label}
              {active === l.id && (
                <motion.span layoutId="nav-underline" className="absolute left-3 right-3 -bottom-0.5 h-0.5 bg-brand-blue rounded-full" />
              )}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <BrutalButton onClick={openLead} variant="red" className="!px-4 !py-2.5 !min-h-[44px] !text-sm" data-testid="nav-join-button">
            <span className="hidden sm:inline">Join the Current Batch</span>
            <span className="sm:hidden">Join Now</span>
          </BrutalButton>
          <button
            onClick={() => setMenu((m) => !m)}
            className="lg:hidden h-11 w-11 flex items-center justify-center rounded-xl border-2 border-brand-black bg-white"
            aria-label="Menu"
            data-testid="nav-hamburger"
          >
            {menu ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {menu && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="lg:hidden overflow-hidden bg-brand-cream border-b-2 border-brand-black/10"
            data-testid="nav-mobile-menu"
          >
            <div className="px-5 py-4 flex flex-col gap-1">
              {NAV_LINKS.map((l) => (
                <button key={l.id} onClick={() => go(l.id)} data-testid={`nav-mobile-link-${l.id}`}
                  className="text-left px-3 py-3 rounded-xl font-semibold hover:bg-white transition-colors">
                  {l.label}
                </button>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
